/* eslint sort-keys:["warn", "asc", { "minKeys": 3 }] */

import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import type { GameStatus } from '#src/__generated__/globalTypes.ts';
import {
  CardPackCategory,
  ReferralState,
  SeasonEligibility,
  ShopItemType,
} from '#src/__generated__/globalTypes.ts';
import type {
  AlgoliaCardIndexesName,
  GallerySortName,
  PrintableI18nPlayerSortName,
} from '#src/contexts/config/index.ts';

export const CAPTAIN = 'C';

export const glossary = defineMessages({
  accept: {
    defaultMessage: 'Accept',
  },
  accountEmail: {
    defaultMessage: 'Account Email',
  },
  accountSecurityOverview: {
    defaultMessage: 'Account Security Overview',
  },
  add: {
    defaultMessage: 'Add',
    description: 'Glossary - Add',
  },
  addPhoneNumber: {
    defaultMessage: 'Add phone number',
  },
  addRecoveryEmailBeforeTwoFa: {
    defaultMessage:
      'Add and verify a recovery email before enabling two-factor authentication.',
  },
  all: {
    defaultMessage: 'All',
  },
  applyFilters: {
    defaultMessage: 'Apply filters',
  },
  auction: {
    defaultMessage: 'Auction',
  },
  auctionIdle: {
    defaultMessage: 'Idle',
    description: 'Auction status when the user has not bid on it',
  },
  auctionOutbid: {
    defaultMessage: 'Outbid',
  },
  auctionWinning: {
    defaultMessage: 'Winning',
  },
  back: {
    defaultMessage: 'Back',
  },
  bestGame: {
    defaultMessage: 'Best game',
  },
  bids: {
    defaultMessage: 'Bids',
  },
  biweekly: {
    defaultMessage: 'Biweekly',
  },
  boxScore: {
    defaultMessage: 'Box Score',
  },
  buy: {
    defaultMessage: 'Buy',
  },
  buyGems: {
    defaultMessage: 'Buy Gems',
  },
  buyLowest: {
    defaultMessage: 'Buy lowest price',
  },
  buyNow: {
    defaultMessage: 'Buy now',
  },
  buyWithName: {
    defaultMessage: 'Buy {name}',
  },
  cancel: {
    defaultMessage: 'Cancel',
  },
  canceled: {
    defaultMessage: 'Canceled',
    description: 'Glossary - Canceled',
  },
  card: {
    defaultMessage: 'Card',
  },
  cards: {
    defaultMessage: 'Cards',
    description: 'Glossary - Cards',
  },
  cash: {
    defaultMessage: 'Cash',
  },
  cashRewards: {
    defaultMessage: 'Cash Rewards',
  },
  caution: {
    defaultMessage: 'Caution',
  },
  claim: {
    defaultMessage: 'Claim',
  },
  claimMyReward: {
    defaultMessage: 'Claim my Reward',
  },
  claimNow: {
    defaultMessage: 'Claim Now',
  },
  claimed: {
    defaultMessage: 'Claimed',
  },
  close: {
    defaultMessage: 'Close',
  },
  closed: {
    defaultMessage: 'Closed',
  },
  closesIn: {
    defaultMessage: 'Closes in {time}',
  },
  clubs: {
    defaultMessage: 'Clubs',
  },
  collect: {
    defaultMessage: 'Collect',
  },
  commonEssence: {
    defaultMessage: 'Common Essence',
  },
  completed: {
    defaultMessage: 'Completed',
  },
  confirm: {
    defaultMessage: 'Confirm',
  },
  continue: {
    defaultMessage: 'Continue',
  },
  copiedToClipboard: {
    defaultMessage: 'Copied to clipboard!',
  },
  copy: {
    defaultMessage: 'Copy',
  },
  cost: {
    defaultMessage: 'Cost',
  },
  countryOfResidence: {
    defaultMessage: 'Country of residence',
  },
  create: {
    defaultMessage: 'Create',
  },
  created: {
    defaultMessage: 'Created {date}',
  },
  daily: {
    defaultMessage: 'Daily',
  },
  date: {
    defaultMessage: 'Date',
  },
  dateOfBirth: {
    defaultMessage: 'Date of birth',
  },
  decline: {
    defaultMessage: 'Decline',
  },
  declined: {
    defaultMessage: 'Declined',
  },
  delete: {
    defaultMessage: 'Delete',
  },
  deposit: {
    defaultMessage: 'Deposit',
    description: 'Glossary - Deposit',
  },
  depositAction: {
    defaultMessage: 'Deposit',
    description: 'Glossary - DepositAction',
  },
  determinative: {
    defaultMessage: 'a {type}',
  },
  determinativeBox: {
    defaultMessage: 'a Box',
  },
  determinativeCard: {
    defaultMessage: 'a Card',
  },
  determinativeCardPack: {
    defaultMessage: 'a Card Pack',
  },
  determinativeEssence: {
    defaultMessage: 'some Essence',
  },
  determinativeExperiences: {
    defaultMessage: 'an Experience',
  },
  determinativeGems: {
    defaultMessage: 'some Gems',
  },
  determinativeMarketCredit: {
    defaultMessage: 'some Market Credit',
  },
  determinativeMoney: {
    defaultMessage: 'some Money',
  },
  determinativeTickets: {
    defaultMessage: 'tickets',
  },
  disabled: {
    defaultMessage: 'Disabled',
  },
  discount: {
    defaultMessage: 'Discount',
  },
  discover: {
    defaultMessage: 'Discover',
  },
  dismiss: {
    defaultMessage: 'Dismiss',
  },
  doItLater: {
    defaultMessage: 'I’ll do it later',
  },
  done: {
    defaultMessage: 'Done',
  },
  download: {
    defaultMessage: 'Download',
  },
  duration: {
    defaultMessage: 'Duration',
  },
  edit: {
    defaultMessage: 'Edit',
  },
  enabled: {
    defaultMessage: 'Enabled',
  },
  equipped: {
    defaultMessage: 'Equipped',
  },
  essence: {
    defaultMessage: 'Essence',
    description: 'Glossary - Essence',
  },
  essenceLabel: {
    defaultMessage:
      'Essence {quantity, plural, one {capsule} other {capsules}}',
  },
  essenceRarityAmountLabel: {
    defaultMessage: '{amount} {rarity} Essence',
  },
  essenceRarityLabel: {
    defaultMessage:
      '{rarity} Essence {quantity, plural, one {capsule} other {capsules}}',
  },
  expired: {
    defaultMessage: 'Expired',
    description: 'Glossary - Expired',
  },
  explore: {
    defaultMessage: 'Explore',
  },
  failed: {
    defaultMessage: 'Failed',
  },
  favorites: {
    defaultMessage: 'Favorites',
    description: 'Glossary - Favorites',
  },
  fewMoments: {
    defaultMessage: 'a few moments',
  },
  filterBy: {
    defaultMessage: 'Filter by',
  },
  find: {
    defaultMessage: 'Find',
  },
  firstName: {
    defaultMessage: 'First name',
  },
  follow: {
    defaultMessage: 'Follow',
  },
  followed: {
    defaultMessage: 'Followed',
  },
  followers: {
    defaultMessage: 'Followers',
  },
  following: {
    defaultMessage: 'Following',
  },
  forYou: {
    defaultMessage: 'For You',
  },
  games: {
    defaultMessage: 'Games',
  },
  getStarted: {
    defaultMessage: 'Get Started',
  },
  global: {
    defaultMessage: 'Global',
  },
  goToMarket: {
    defaultMessage: 'Go to market',
  },
  gotIt: {
    defaultMessage: 'Got it',
  },
  howItWorks: {
    defaultMessage: 'How it works',
  },
  howToPlay: {
    defaultMessage: 'How to Play',
    description: 'Glossary - HowToPlay',
  },
  insufficientBalance: {
    defaultMessage: 'Insufficient balance',
  },
  introducing: {
    defaultMessage: 'Introducing',
  },
  invite: {
    defaultMessage: 'Invite',
  },
  join: {
    defaultMessage: 'Join',
  },
  joinForFree: {
    defaultMessage: 'Join for free',
  },
  landingHero: {
    defaultMessage: 'Scout. Play. Win.',
  },
  landingHeroWallet: {
    defaultMessage: 'Your Name. Your Legend.',
  },
  landingHeroWalletPlaceholder: {
    defaultMessage: 'Your Manager name',
  },
  lastName: {
    defaultMessage: 'Last name',
  },
  latest: {
    defaultMessage: 'Latest',
  },
  latestNews: {
    defaultMessage: 'Latest News',
  },
  learnMore: {
    defaultMessage: 'Learn more',
  },
  letsGo: {
    defaultMessage: "Let's go!",
  },
  lineups: {
    defaultMessage: 'Lineups',
    description: 'Glossary - Lineups',
  },
  listings: {
    defaultMessage: 'Listings',
  },
  loadMore: {
    defaultMessage: 'Load more',
  },
  loading: {
    defaultMessage: 'Loading',
  },
  locked: {
    defaultMessage: 'Locked',
  },
  logOut: {
    defaultMessage: 'Log Out',
  },
  lowestPrice: {
    defaultMessage: 'Lowest price',
  },
  manager: {
    defaultMessage: 'Manager',
  },
  matchInfo: {
    defaultMessage: 'Match info',
  },
  matchTickets: {
    defaultMessage: 'Match Tickets',
  },
  matches: {
    defaultMessage: 'Matches',
    description: 'Glossary - Matches',
  },
  max: {
    defaultMessage: 'Max',
  },
  more: {
    defaultMessage: 'More',
    description: 'Glossary - More',
  },
  mvp: {
    defaultMessage: 'MVP',
  },
  myCircle: {
    defaultMessage: 'My Circle',
  },
  myCollections: {
    defaultMessage: 'My Collections',
  },
  mySearches: {
    defaultMessage: 'My searches',
  },
  name: {
    defaultMessage: 'Name',
  },
  nationality: {
    defaultMessage: 'Nationality',
    description: 'Glossary - Nationality',
  },
  new: {
    defaultMessage: 'New',
    description: 'Glossary - New',
  },
  next: {
    defaultMessage: 'Next',
  },
  no: {
    defaultMessage: 'No',
  },
  noCardEntry: {
    defaultMessage: 'No Card Entry',
  },
  noGame: {
    defaultMessage: 'No game',
  },
  noResultsFound: {
    defaultMessage: 'No results found',
  },
  noTeamForThisWeek: {
    defaultMessage: 'No team registered for this week.',
  },
  noThanks: {
    defaultMessage: 'No, thanks',
  },
  notEnoughGems: {
    defaultMessage: 'Not enough gems',
  },
  notMajorError: {
    defaultMessage: 'You must be over 18.',
  },
  now: {
    defaultMessage: 'now',
  },
  officiallyLicensed: {
    defaultMessage: 'Officially licensed product of <b>{displayName}</b>',
  },
  officiallyLicensedProduct: {
    defaultMessage: 'Officially licensed product',
  },
  ok: {
    defaultMessage: 'OK',
  },
  opensIn: {
    defaultMessage: 'Opens in {time}',
  },
  or: {
    defaultMessage: 'or',
  },
  ordinal: {
    defaultMessage:
      '{ordinal, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}',
  },
  ordinalStyled: {
    defaultMessage:
      '{ordinal, selectordinal, one {#<sup>st</sup>} two {#<sup>nd</sup>} few {#<sup>rd</sup>} other {#<sup>th</sup>}}',
  },
  owned: {
    defaultMessage: 'Owned',
  },
  payWith: {
    defaultMessage: 'Pay with',
  },
  percentWithSup: {
    defaultMessage: '{percent}<sup>%</sup>',
  },
  play: {
    defaultMessage: 'Play',
    description: 'Glossary - Play',
  },
  playInPro: {
    defaultMessage: 'Play in Pro!',
  },
  playNow: {
    defaultMessage: 'Play now',
  },
  players: {
    defaultMessage: 'Players',
    description: 'Glossary - Players',
  },
  poweredBy: {
    defaultMessage: 'Powered by',
  },
  previous: {
    defaultMessage: 'Previous',
  },
  private: {
    defaultMessage: 'Private',
  },
  projection: {
    defaultMessage: 'Projection',
  },
  projections: {
    defaultMessage: 'Projections',
  },
  public: {
    defaultMessage: 'Public',
  },
  quickPlay: {
    defaultMessage: 'Quick Play',
  },
  quietWeek: {
    defaultMessage: 'Quiet week',
  },
  ready: {
    defaultMessage: 'Ready',
  },
  recoveryEmail: {
    defaultMessage: 'Recovery email',
  },
  reject: {
    defaultMessage: 'Reject',
  },
  relatedClubs: {
    defaultMessage: 'Related clubs',
  },
  remove: {
    defaultMessage: 'Remove',
  },
  resendVerificationCode: {
    defaultMessage: 'Resend verification email',
  },
  reward: {
    defaultMessage: 'Reward',
  },
  rewards: {
    defaultMessage: 'Rewards',
  },
  save: {
    defaultMessage: 'Save',
  },
  savePreferences: {
    defaultMessage: 'Save preferences',
  },
  search: {
    defaultMessage: 'Search',
    description: 'Glossary - Search',
  },
  searchByPlayerClubLeague: {
    defaultMessage: 'Search by Player, Club or League',
  },
  seeDetails: {
    defaultMessage: 'See details',
  },
  seeTotalPrizePool: {
    defaultMessage: 'See total prize pool',
  },
  select: {
    defaultMessage: 'Select',
  },
  send: {
    defaultMessage: 'Send',
  },
  setEdition: {
    defaultMessage: '{set} Edition',
  },
  share: {
    defaultMessage: 'Share',
  },
  showLess: {
    defaultMessage: 'Show less',
  },
  showMore: {
    defaultMessage: 'Show more',
  },
  signedJerseys: {
    defaultMessage: 'Signed jerseys',
  },
  signin: {
    defaultMessage: 'Sign in',
  },
  signup: {
    defaultMessage: 'Sign up',
  },
  skin: {
    defaultMessage: 'Skin',
  },
  skip: {
    defaultMessage: 'Skip',
  },
  soldBy: {
    defaultMessage: 'Sold by',
  },
  squads: {
    defaultMessage: 'Squads',
  },
  start: {
    defaultMessage: 'Start',
  },
  startingAt: {
    defaultMessage: 'Starting at',
  },
  startingSoon: {
    defaultMessage: 'Starting soon',
  },
  stats: {
    defaultMessage: 'Stats',
    description: 'Glossary - Stats',
  },
  step: {
    defaultMessage: 'Step',
  },
  submit: {
    defaultMessage: 'Submit',
  },
  submitLineup: {
    defaultMessage: 'Submit lineup',
  },
  termsApply: {
    defaultMessage: 'Terms apply',
  },
  tier: {
    defaultMessage: 'Tier',
    description: 'Glossary - Tier',
  },
  today: {
    defaultMessage: 'Today',
  },
  topPlayers: {
    defaultMessage: 'Top players',
  },
  trade: {
    defaultMessage: 'Trade',
  },
  transactions: {
    defaultMessage: 'Transactions',
    description: 'Glossary - Transactions',
  },
  transferInProgress: {
    defaultMessage: 'Transfer in progress',
  },
  twofa: {
    defaultMessage: 'Two-factor Authentication',
  },
  undo: {
    defaultMessage: 'Undo',
  },
  unfollow: {
    defaultMessage: 'Unfollow',
  },
  update: {
    defaultMessage: 'Update',
  },
  verify: {
    defaultMessage: 'Verify',
  },
  viewAll: {
    defaultMessage: 'View All',
  },
  viewDetails: {
    defaultMessage: 'View details',
  },
  viewGallery: {
    defaultMessage: 'View gallery',
  },
  viewMore: {
    defaultMessage: 'View More',
  },
  warning: {
    defaultMessage: 'Warning',
  },
  withdraw: {
    defaultMessage: 'Withdraw',
  },
  xp: {
    defaultMessage: 'XP',
  },
  yes: {
    defaultMessage: 'Yes',
  },
  you: {
    defaultMessage: 'You',
  },
  zipCode: {
    defaultMessage: 'Zip Code',
  },
});

export const tradeLabels = defineMessages({
  counterOfferWith: {
    defaultMessage: "Counter {nickname}'s trade",
  },
  tradeWith: {
    defaultMessage: 'Trade with {nickname}',
  },
  usernameSends: {
    defaultMessage: '<b>{username}</b> sends',
  },
  youReceive: {
    defaultMessage: 'You receive',
  },
  youReceiveWithAvatar: {
    defaultMessage: '<span>{nickname}</span> sends',
  },
  youSend: {
    defaultMessage: 'You send',
  },
});

export const emptyStateLabels = defineMessages({
  noLineupsEntered: {
    defaultMessage: 'No Lineups Entered',
  },
});

export const navLabels = defineMessages({
  about: {
    defaultMessage: 'About',
  },
  allCards: {
    defaultMessage: 'All cards',
  },
  amateurPacks: {
    defaultMessage: 'Amateur Packs',
  },
  apiTerms: {
    defaultMessage: 'API Terms',
  },
  arena: {
    defaultMessage: 'Arena',
  },
  arenaClassic: {
    defaultMessage: 'Arena Classic',
    description: 'Nav Labels - Arena Classic',
  },
  arenaInSeason: {
    defaultMessage: 'Arena In-season',
    description: 'Nav Labels - Arena In-season',
  },
  auctions: {
    defaultMessage: 'Auctions',
  },
  blog: {
    defaultMessage: 'Blog',
  },
  calendar: {
    defaultMessage: 'Calendar',
  },
  cardsSupply: {
    defaultMessage: 'Cards supply',
  },
  careers: {
    defaultMessage: 'Careers',
  },
  chase: {
    defaultMessage: 'Chase',
  },
  clubHistory: {
    defaultMessage: 'Club History',
  },
  clubHonors: {
    defaultMessage: 'Club Honors',
  },
  clubshop: {
    defaultMessage: 'Club Shop',
  },
  collections: {
    defaultMessage: 'Collections',
    description: 'Nav Labels - Collections',
  },
  commonPacks: {
    defaultMessage: 'Common Packs',
  },
  competitions: {
    defaultMessage: 'Competitions',
    description: 'Nav Labels - Competitions',
  },
  contentCreatorTool: {
    defaultMessage: 'Content Creator Tool',
  },
  cookiePolicy: {
    defaultMessage: 'Cookie Policy',
  },
  coverage: {
    defaultMessage: 'Coverage',
  },
  creatorProgram: {
    defaultMessage: 'Creator Program',
  },
  faq: {
    defaultMessage: 'FAQ',
  },
  fixtures: {
    defaultMessage: 'Fixtures',
  },
  footballFantasy: {
    defaultMessage: 'Fantasy Football',
  },
  footballMarket: {
    defaultMessage: 'Football Market',
  },
  gallery: {
    defaultMessage: 'Gallery',
  },
  gameRules: {
    defaultMessage: 'Game Rules',
  },
  gamingArena: {
    defaultMessage: 'Play',
    description: 'Nav Labels - GamingArena',
  },
  gamingCompetitions: {
    defaultMessage: 'Competitions',
    description: 'Nav Labels - GamingCompetitions',
  },
  globalCup: {
    defaultMessage: 'Global Cup',
  },
  hallOfFame: {
    defaultMessage: 'Hall of Fame',
    description: 'Nav Labels - HallOfFame',
  },
  help: {
    defaultMessage: 'Help',
  },
  helpCenter: {
    defaultMessage: 'Help Center',
  },
  history: {
    defaultMessage: 'History',
  },
  home: {
    defaultMessage: 'Home',
    description: 'Nav Labels - Home',
  },
  howToPlay: {
    defaultMessage: 'How to Play',
    description: 'Nav Labels - HowToPlay',
  },
  inviteFriends: {
    defaultMessage: 'Invite Friends',
  },
  leaderboards: {
    defaultMessage: 'Leaderboards',
    description: 'Nav Labels - Leaderboards',
  },
  leagues: {
    defaultMessage: 'Leagues',
  },
  legalNotice: {
    defaultMessage: 'Legal Notice',
  },
  licensedClubs: {
    defaultMessage: 'Licensed Clubs',
  },
  licensedPartners: {
    defaultMessage: 'Licensed Partners',
  },
  lineups: {
    defaultMessage: 'Lineups',
    description: 'Nav Labels - Lineups',
  },
  live: {
    defaultMessage: 'Live',
    description: 'Nav Labels - Live',
  },
  lobby: {
    defaultMessage: 'Lobby',
  },
  managerAssistant: {
    defaultMessage: 'Manager Assistant',
  },
  market: {
    defaultMessage: 'Market',
    description: 'Nav Labels - Market',
  },
  marketplaceTerms: {
    defaultMessage: 'Marketplace Terms',
  },
  matches: {
    defaultMessage: 'Matches',
    description: 'Nav Labels - Matches',
  },
  matchups: {
    defaultMessage: 'Matchups',
  },
  members: {
    defaultMessage: 'Members',
  },
  mercato: {
    defaultMessage: 'Mercato',
  },
  missions: {
    defaultMessage: 'Missions',
    description: 'Nav Labels - Missions',
  },
  mlbFantasy: {
    defaultMessage: 'Fantasy MLB',
  },
  mlbMarket: {
    defaultMessage: 'MLB Market',
  },
  mlbSignUp: {
    defaultMessage: 'MLB: Sign up',
  },
  mobileDebugScreen: {
    defaultMessage: 'Debug Screen',
  },
  more: {
    defaultMessage: 'More',
    description: 'Nav Labels - More',
  },
  myAuctions: {
    defaultMessage: 'My Auctions',
  },
  myCards: {
    defaultMessage: 'My Cards',
    description: 'Nav Labels - MyCards',
  },
  myClub: {
    defaultMessage: 'My Club',
  },
  myFollows: {
    defaultMessage: 'Follows',
  },
  myGallery: {
    defaultMessage: 'My Cards',
    description: 'Nav Labels - MyGallery',
  },
  myInstantBuys: {
    defaultMessage: 'My Instant Buys',
  },
  myLineups: {
    defaultMessage: 'My Lineups',
  },
  myListings: {
    defaultMessage: 'My Listings',
  },
  myMarketActivity: {
    defaultMessage: 'My Market Activity',
  },
  myMatches: {
    defaultMessage: 'My Matches',
  },
  myOffersReceived: {
    defaultMessage: 'Offers Received',
  },
  myOffersSent: {
    defaultMessage: 'Offers Sent',
  },
  myPlayerNotifications: {
    defaultMessage: 'Player Notifications',
  },
  myProLineups: {
    defaultMessage: 'My Pro Lineups',
  },
  myPurchases: {
    defaultMessage: 'Manager Sales',
    description: 'Nav Labels - MyPurchases',
  },
  mySorare: {
    defaultMessage: 'My Sorare',
  },
  mySquad: {
    defaultMessage: 'My Squad',
  },
  myTransactions: {
    defaultMessage: 'Transactions',
    description: 'Nav Labels - MyTransactions',
  },
  nbaFantasy: {
    defaultMessage: 'Fantasy NBA',
  },
  nbaMarket: {
    defaultMessage: 'NBA Market',
  },
  new: {
    defaultMessage: "What's New",
  },
  newSeason: {
    defaultMessage: 'New Season',
  },
  notifications: {
    defaultMessage: 'Notifications',
  },
  overview: {
    defaultMessage: 'Overview',
  },
  packs: {
    defaultMessage: 'Packs',
  },
  play: {
    defaultMessage: 'Play',
    description: 'Nav Labels - Play',
  },
  players: {
    defaultMessage: 'Players',
    description: 'Nav Labels - Players',
  },
  playoffs: {
    defaultMessage: 'Playoffs',
  },
  press: {
    defaultMessage: 'Press',
  },
  privacyPolicy: {
    defaultMessage: 'Privacy Policy',
  },
  privateLeague: {
    defaultMessage: 'Private League',
  },
  privateLeagues: {
    defaultMessage: 'Private Leagues',
  },
  prizePool: {
    defaultMessage: 'Prize Pool',
  },
  pro: {
    defaultMessage: 'Pro',
    description: 'Nav Labels - Pro',
  },
  profile: {
    defaultMessage: 'Profile',
  },
  purchases: {
    defaultMessage: 'Manager Sales',
    description: 'Nav Labels - Purchases',
  },
  salesHistory: {
    defaultMessage: 'Sales history',
  },
  schedule: {
    defaultMessage: 'Schedule',
  },
  scores: {
    defaultMessage: 'Scores',
  },
  search: {
    defaultMessage: 'Search',
    description: 'Nav Labels - Search',
  },
  set: {
    defaultMessage: 'Set',
  },
  settings: {
    defaultMessage: 'Settings',
  },
  shop: {
    defaultMessage: 'Shop',
  },
  standings: {
    defaultMessage: 'Standings',
  },
  stats: {
    defaultMessage: 'Stats',
    description: 'Nav Labels - Stats',
  },
  team: {
    defaultMessage: 'Players',
    description: 'Nav Labels - Team',
  },
  terms: {
    defaultMessage: 'Terms & Conditions',
  },
  termsAndPrivacy: {
    defaultMessage: 'Terms & Privacy',
  },
  tradeMarket: {
    defaultMessage: 'Trade Market',
  },
  transferMarket: {
    defaultMessage: 'Market',
    description: 'Nav Labels - TransferMarket',
  },
  trending: {
    defaultMessage: 'Trending',
  },
  userClub: {
    defaultMessage: "{username}'s club",
  },
  vault: {
    defaultMessage: 'Vault',
  },
});

export const galleryTabs = defineMessages({
  cardCollections: {
    defaultMessage: 'Collections',
    description: 'Gallery Tabs - CardCollections',
  },
  cards: {
    defaultMessage: 'Cards',
    description: 'Gallery Tabs - Cards',
  },
});

export const userAttributes = defineMessages({
  clubName: {
    defaultMessage: 'Club name',
  },
  currency: {
    defaultMessage: 'Conversion',
  },
  email: {
    defaultMessage: 'Email',
  },
  fiatCurrency: {
    defaultMessage: 'Currency',
  },
  nickname: {
    defaultMessage: 'Username',
  },
  otpDisableAttempts: {
    defaultMessage: 'Six-digit code OR recovery code',
  },
  password: {
    defaultMessage: 'Password',
  },
  phoneNumber: {
    defaultMessage: 'Phone number',
  },
  status: {
    defaultMessage: 'Status',
  },
});

export const fantasy = defineMessages({
  addCircle: {
    defaultMessage: 'Add to Circle',
  },
  addTeam: {
    defaultMessage: 'Add Team',
  },
  addToList: {
    defaultMessage: 'Add to list',
  },
  allAround: {
    defaultMessage: 'All around',
  },
  allAroundScore: {
    defaultMessage: 'All around score',
  },
  allAroundShort: {
    defaultMessage: 'AA',
  },
  alreadyJoined: {
    defaultMessage: 'Already joined',
  },
  away: {
    defaultMessage: 'Away',
  },
  bonus: {
    defaultMessage: 'Bonus',
    description: 'Fantasy - Bonus',
  },
  bonuses: {
    defaultMessage: 'Bonuses',
  },
  boost: {
    defaultMessage: 'Boost',
  },
  boost5x: {
    defaultMessage: '5x Boost',
  },
  boxOdds: {
    defaultMessage: 'Box Odds',
  },
  boxes: {
    defaultMessage: 'Boxes',
  },
  captain: {
    defaultMessage: 'Captain',
  },
  captainDescription: {
    defaultMessage:
      'The player you select as captain will get a {bonus, number, percent} bonus to their score.',
  },
  cardBonus: {
    defaultMessage: 'Card Bonus',
  },
  collectionMilestones: {
    defaultMessage: 'Collection milestones',
  },
  completionMilestones: {
    defaultMessage: 'Completion milestones',
  },
  composeLineup: {
    defaultMessage: 'Compose your lineup',
  },
  computed: {
    defaultMessage: 'Waiting',
  },
  confirmDelete: {
    defaultMessage: 'Are you sure you want to delete your team?',
  },
  confirmYourLineup: {
    defaultMessage: 'Confirm your lineup',
  },
  credits: {
    defaultMessage: 'Credits',
  },
  cumulativeLeaderboard: {
    defaultMessage: 'Cumulative leaderboard',
  },
  decisive: {
    defaultMessage: 'Decisive',
  },
  decisiveScore: {
    defaultMessage: 'Decisive score',
  },
  decisiveScoreShort: {
    defaultMessage: 'DS',
  },
  defeat: {
    defaultMessage: 'Defeat',
  },
  discoverCompetitions: {
    defaultMessage: 'Discover Competitions',
  },
  discoverNewCompetitions: {
    defaultMessage: 'Discover new competitions',
  },
  division: {
    defaultMessage: 'Div. {division}',
  },
  draft: {
    defaultMessage: 'Draft',
  },
  draw: {
    defaultMessage: 'Draw',
  },
  editLineup: {
    defaultMessage: 'Edit Lineup',
  },
  eligibleCards: {
    defaultMessage: 'Eligible cards',
  },
  ended: {
    defaultMessage: 'Ended',
  },
  entered: {
    defaultMessage: 'Entered',
  },
  essenceShort: {
    defaultMessage: 'Essence',
    description: 'Fantasy - EssenceShort',
  },
  essencesShort: {
    defaultMessage: 'Essences',
  },
  extraDraft: {
    defaultMessage: 'Extra Draft',
  },
  final: {
    defaultMessage: 'Final',
  },
  freeToPlay: {
    defaultMessage: 'Free-to-play',
  },
  fullTime: {
    defaultMessage: 'FT',
  },
  gainOnProjection: {
    defaultMessage: 'Gain on Proj.',
  },
  game: {
    defaultMessage: 'Game',
  },
  gameCenter: {
    defaultMessage: 'Game Center',
  },
  gameMode: {
    defaultMessage: 'Game mode',
  },
  gameWeek: {
    defaultMessage: 'Game Week {gameWeek}',
  },
  gameplay: {
    defaultMessage: 'Gameplay',
  },
  gw: {
    defaultMessage: 'GW {week}',
  },
  hideTeam: {
    defaultMessage: 'Hide Team',
  },
  home: {
    defaultMessage: 'Home',
    description: 'Fantasy - Home',
  },
  hotStreak: {
    defaultMessage: 'Hot Streak',
  },
  hotStreaks: {
    defaultMessage: 'Hot Streaks',
  },
  inArena: {
    defaultMessage: 'In Arena',
  },
  inCircle: {
    defaultMessage: 'In My Circle',
  },
  incomplete: {
    defaultMessage: 'Incomplete',
  },
  joined: {
    defaultMessage: 'Joined',
  },
  l10: {
    defaultMessage: 'L10',
  },
  lastScores: {
    defaultMessage: 'Last scores',
  },
  leaderboard: {
    defaultMessage: 'Leaderboard',
  },
  levelNumber: {
    defaultMessage: 'LVL {level}',
  },
  levelUp: {
    defaultMessage: 'Level Up',
  },
  lineups: {
    defaultMessage: 'Lineups',
    description: 'Fantasy - Lineups',
  },
  lineupsCount: {
    defaultMessage: '{lineupsCount, plural, one {1 Lineup} other {# Lineups}}',
  },
  lineupsRelativeCount: {
    defaultMessage:
      '{lineupsCount}/{teamsCap, plural, one {1 Lineup} other {# Lineups}}',
  },
  listedCard: {
    defaultMessage: 'Listed on Market',
  },
  live: {
    defaultMessage: 'Live',
    description: 'Fantasy - Live',
  },
  loss: {
    defaultMessage: 'Loss',
  },
  matchCenter: {
    defaultMessage: 'Match Center',
  },
  missions: {
    defaultMessage: 'Missions',
    description: 'Fantasy - Missions',
  },
  nameLeaderboard: {
    defaultMessage: '{displayName} Leaderboard',
  },
  notEntered: {
    defaultMessage: 'Not Entered',
  },
  numberOfmatches: {
    defaultMessage: '{nbMatches, plural, one {# Match} other {# Matches}}',
  },
  objectives: {
    defaultMessage: 'Objectives',
  },
  ordinalPoints: {
    defaultMessage: '{score}<sup>pts</sup>',
  },
  participants: {
    defaultMessage:
      '{lineupsCount, plural, one {# participant} other {# participants}}',
  },
  past: {
    defaultMessage: 'Past',
  },
  pastCompetitions: {
    defaultMessage: 'Past Competitions',
  },
  percentagePlayed: {
    defaultMessage: '{percent, number, percent} played',
  },
  pickName: {
    defaultMessage: 'Pick {name}',
  },
  playerPerformance: {
    defaultMessage: 'Player performance',
  },
  playerScore: {
    defaultMessage: 'Player Score',
  },
  playersPermittedFrom: {
    defaultMessage: 'Players permitted from',
  },
  podiumRank: {
    defaultMessage:
      '{rank, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} Place',
  },
  points: {
    defaultMessage: '{score, number, ::.00 rounding-mode-down} pts',
  },
  pointsCap: {
    defaultMessage: '{value, number} points cap',
  },
  pointsWithStyledOneDecimalScore: {
    defaultMessage: '<span>{score, number, ::.0 rounding-mode-down}</span> pts',
  },
  pointsWithStyledScore: {
    defaultMessage:
      '<span>{score, number, ::.00 rounding-mode-down}</span> pts',
  },
  pointsWithStyledScoreWithoutFormat: {
    defaultMessage: '<span>{score}</span> pts',
  },
  pointsWithoutFormat: {
    defaultMessage: '{score} pts',
  },
  prizePool: {
    defaultMessage: 'Prize pool',
  },
  prizes: {
    defaultMessage: 'Prizes',
  },
  proHistory: {
    defaultMessage: 'Pro History',
  },
  projectedRewards: {
    defaultMessage: 'Projected Rewards',
  },
  projectedScore: {
    defaultMessage: 'Projected Score',
  },
  projectedScoreShort: {
    defaultMessage: 'Proj. Score',
  },
  promotion: {
    defaultMessage: 'Promotion',
  },
  pts: {
    defaultMessage: 'pts',
  },
  rank: {
    defaultMessage: 'Rank',
  },
  ranked: {
    defaultMessage: 'Ranked',
  },
  reachTargetPoints: {
    defaultMessage: 'Reach {target} points',
  },
  redraft: {
    defaultMessage: 'Redraft',
  },
  referralReward: {
    defaultMessage: 'Referral Reward',
  },
  registerLineup: {
    defaultMessage: 'Register Lineup',
  },
  registerNewTeam: {
    defaultMessage: 'Register a new team',
  },
  relegation: {
    defaultMessage: 'Relegation',
  },
  removeCard: {
    defaultMessage: 'Playing this card would remove it from the Market',
  },
  removeCircle: {
    defaultMessage: 'Remove from Circle',
  },
  reveal: {
    defaultMessage: 'Reveal',
  },
  revealCards: {
    defaultMessage: 'Reveal Cards',
  },
  rewardsBoost: {
    defaultMessage: 'Rewards boost',
  },
  rewardsBoostPct: {
    defaultMessage: '+{pct} rewards',
  },
  rewardsPerRank: {
    defaultMessage: 'Rewards per rank',
  },
  rewardsPerTarget: {
    defaultMessage: 'Rewards per target',
  },
  rewardsPerThreshold: {
    defaultMessage: 'Rewards per threshold',
  },
  room: {
    defaultMessage: 'Room {division}',
  },
  rules: {
    defaultMessage: 'Rules',
  },
  safeZone: {
    defaultMessage: 'Safe zone',
  },
  scarcities: {
    defaultMessage: 'Scarcities',
  },
  score: {
    defaultMessage: 'Score',
  },
  scoutPopularPlayer: {
    defaultMessage: 'Scout player',
  },
  scoutPopularPosition: {
    defaultMessage: 'Scout {position}',
  },
  scrap: {
    defaultMessage: 'Scrap',
  },
  seal: {
    defaultMessage: 'Seal',
  },
  selectPlayer: {
    defaultMessage: 'Select player',
  },
  selectYourPlayer: {
    defaultMessage: 'Select your player',
  },
  selectYourPosition: {
    defaultMessage: 'Select your {position}',
  },
  setLineup: {
    defaultMessage: 'Set Lineup',
  },
  shortCumulativeLeaderboard: {
    defaultMessage: 'Cumulative',
  },
  startSealing: {
    defaultMessage: 'Start Sealing',
  },
  suggest: {
    defaultMessage: 'Suggest',
  },
  suggestLineup: {
    defaultMessage: 'Suggest Lineup',
  },
  swap: {
    defaultMessage: 'Swap',
  },
  target: {
    defaultMessage: 'Target',
  },
  targetNumber: {
    defaultMessage: 'Target {targetNb}',
  },
  targetPoints: {
    defaultMessage: '{target, number}+ points',
  },
  team: {
    defaultMessage: 'Team',
    description: 'Fantasy - Team',
  },
  teamVersus: {
    defaultMessage: '{homeTeam} vs {awayTeam}',
  },
  total: {
    defaultMessage: 'Total',
    description: 'Fantasy - Total',
  },
  totalPointsAboveCap: {
    defaultMessage: 'Total points above cap',
  },
  totalPrizePool: {
    defaultMessage: 'Total Prize Pool',
  },
  training: {
    defaultMessage: 'Training',
  },
  trainingTeams: {
    defaultMessage: 'Training Teams',
  },
  undoNumber: {
    defaultMessage: 'Undo <span>({value, number})</span>',
  },
  upcoming: {
    defaultMessage: 'Upcoming',
  },
  victory: {
    defaultMessage: 'Victory',
  },
  viewAllMatches: {
    defaultMessage: 'View All Matches',
  },
  viewLeaderboard: {
    defaultMessage: 'View Leaderboard',
  },
  viewLineup: {
    defaultMessage: 'View Lineup',
  },
  viewPrizePool: {
    defaultMessage: 'View Prize Pool',
  },
  viewRewards: {
    defaultMessage: 'View Rewards',
  },
  viewRewardsPool: {
    defaultMessage: 'View Rewards Pool',
  },
  viewSchedule: {
    defaultMessage: 'View Schedule',
  },
  vs: {
    defaultMessage: 'vs',
  },
  win: {
    defaultMessage: 'Win',
    description: 'Fantasy - Win',
  },
  yourLineups: {
    defaultMessage: 'Your Lineups',
  },
  yourTeam: {
    defaultMessage: 'Your team',
  },
});

export const cardAttributes = defineMessages({
  L10: {
    defaultMessage: 'Last 10 Games',
  },
  L10DecisiveScore: {
    defaultMessage: 'Average decisive score over the last 10 played games',
  },
  L10Score: {
    defaultMessage: 'Average score over the last 10 played games',
  },
  L15: {
    defaultMessage: 'Last 15 Games',
  },
  L15Score: {
    defaultMessage: 'Average score over the last 15 games',
  },
  L40Score: {
    defaultMessage: 'Average score over the last 40 games',
  },
  L5Score: {
    defaultMessage: 'Average score over the last 5 games',
  },
  age: {
    defaultMessage: 'Age',
  },
  averageScore: {
    defaultMessage: 'Average score',
  },
  blockchainID: {
    defaultMessage: 'Blockchain ID',
  },
  bonus: {
    defaultMessage: 'Bonus',
    description: 'Card Attributes - Bonus',
  },
  cardEdition: {
    defaultMessage: 'Card edition',
  },
  cardTeam: {
    defaultMessage: 'Card Team',
  },
  club: {
    defaultMessage: 'Club',
  },
  collectibles: {
    defaultMessage: 'Collectibles',
  },
  country: {
    defaultMessage: 'Country',
    description: 'Card Attributes - Country',
  },
  eligibility: {
    defaultMessage: 'Eligibility',
  },
  height: {
    defaultMessage: 'Height',
  },
  jerseyNumber: {
    defaultMessage: 'Jersey Number',
  },
  last10: {
    defaultMessage: 'Last 10',
  },
  last15: {
    defaultMessage: 'Last 15',
  },
  last40: {
    defaultMessage: 'Last 40',
  },
  last5: {
    defaultMessage: 'Last 5',
  },
  level: {
    defaultMessage: 'Level',
  },
  player: {
    defaultMessage: 'Player',
  },
  position: {
    defaultMessage: 'Position',
    description: 'Card Attributes - Position',
  },
  scarcity: {
    defaultMessage: 'Scarcity',
  },
  sealTitle: {
    defaultMessage: 'Seal Value',
  },
  season: {
    defaultMessage: 'Season',
  },
  seasonAverageScore: {
    defaultMessage: 'Season average score',
    description: 'Card Attributes - SeasonAverageScore',
  },
  serialNumber: {
    defaultMessage: 'Serial Number',
  },
  serie: {
    defaultMessage: 'Serial',
  },
  shortLevel: {
    defaultMessage: 'lvl',
  },
  team: {
    defaultMessage: 'Team',
    description: 'Card Attributes - Team',
  },
  unsealTitle: {
    defaultMessage: 'Unseal Value',
  },
  upcomingGame: {
    defaultMessage: 'This player has an upcoming game.',
  },
  usedInLineup: {
    defaultMessage: 'Used in a lineup',
  },
  xpTitle: {
    defaultMessage: 'Total XP',
  },
  xpValue: {
    defaultMessage: '{xp} XP',
  },
});

export const filters = defineMessages({
  activeClub: {
    defaultMessage: 'Current Club',
  },
  activeNationalTeam: {
    defaultMessage: 'Current National Team',
  },
  activeTeam: {
    defaultMessage: 'Current Team',
  },
  appearances: {
    defaultMessage: 'Games Played',
  },
  averageScore: {
    defaultMessage: 'Average Score',
  },
  bidToEarnLabel: {
    defaultMessage: 'Bid to earn',
  },
  birthplace: {
    defaultMessage: 'Birthplace',
  },
  bundledSale: {
    defaultMessage: 'Sale type',
    description: 'Filters - BundledSale',
  },
  cardLevel: {
    defaultMessage: 'Card Level',
  },
  clearAll: {
    defaultMessage: 'Clear all filters',
  },
  collectibleFilterTitle: {
    defaultMessage: 'Collectible',
  },
  competitionEligibilityFilterTitle: {
    defaultMessage: 'League & Competition',
  },
  creditEligibleFilter: {
    defaultMessage: 'Credit eligible',
  },
  customDeck: {
    defaultMessage: 'Squad',
  },
  customList: {
    defaultMessage: 'List',
  },
  dailyFilterLabel: {
    defaultMessage: 'Playing Game Day',
  },
  duplicate: {
    defaultMessage: 'Duplicate',
  },
  favoriteFilterLabel: {
    defaultMessage: 'Favorites',
    description: 'Filters - FavoriteFilterLabel',
  },
  filters: {
    defaultMessage: 'Filters',
  },
  floorPrices: {
    defaultMessage: 'Price',
    description: 'Filters - FloorPrices',
  },
  gainOnClassicProjection: {
    defaultMessage: 'Next Game Week Gain',
  },
  gainOnDailyProjection: {
    defaultMessage: 'Next Daily Gain',
  },
  gameweekFilterLabel: {
    defaultMessage: 'Playing Game Week',
  },
  jerseySerialLabel: {
    defaultMessage: 'Matching player jersey number',
  },
  lastFifteenAppearances: {
    defaultMessage: 'Last 15 Appearances',
  },
  lastFifteenAverageScore: {
    defaultMessage: 'Last 15 Average Score',
  },
  lastFiveAppearances: {
    defaultMessage: 'Last 5 Appearances',
  },
  lastFiveAverageScore: {
    defaultMessage: 'Last 5 Average Score',
  },
  lastTenAverageScore: {
    defaultMessage: 'Last 10 Average Score',
  },
  latestSeasonLabel: {
    defaultMessage: 'Latest Season',
  },
  legendFilterLabel: {
    defaultMessage: 'Show Legend Cards',
  },
  marketValues: {
    defaultMessage: 'Market value',
    description: 'Filters - MarketValues',
  },
  mlbEligibleLeaderboards: {
    defaultMessage: 'Eligible Leaderboards',
  },
  mlbLastFifteenAverageScore: {
    defaultMessage: 'Last 15 average score',
  },
  mlbSeasonAverageScore: {
    defaultMessage: 'Season average score',
    description: 'Filters - MlbSeasonAverageScore',
  },
  nationality: {
    defaultMessage: 'Nationality',
    description: 'Filters - Nationality',
  },
  nbaShortTenGameAverageScore: {
    defaultMessage: 'L10 Avg.',
  },
  nbaTenGameAverageScore: {
    defaultMessage: 'Ten Game Average',
  },
  noCardsFound: {
    defaultMessage: 'No Cards found',
  },
  noCardsOfTopicFound: {
    defaultMessage: 'No Cards of {topic} found',
  },
  nonPlayableCardsFilterLabel: {
    defaultMessage: 'Show Custom Series Cards',
  },
  notInLineupFilterLabel: {
    defaultMessage: 'Not in lineup',
  },
  onSaleFilterLabel: {
    defaultMessage: 'Cards on sale',
  },
  playingStatusFilterLabel: {
    defaultMessage: 'Playing status',
  },
  price: {
    defaultMessage: 'Price',
    description: 'Filters - Price',
  },
  probablePitchersFilterLabel: {
    defaultMessage: 'Probable Pitchers',
  },
  projectedScoreClassic: {
    defaultMessage: 'Next Game Week projection',
    description: 'Filters - ProjectedScoreClassic',
  },
  projectedScoreDaily: {
    defaultMessage: 'Next Daily projection',
    description: 'Filters - ProjectedScoreDaily',
  },
  promotedCardsFilterTitle: {
    defaultMessage: 'Special Event',
  },
  promotionalEvent: {
    defaultMessage: 'Promotional event',
  },
  saleType: {
    defaultMessage: 'Sale type',
    description: 'Filters - SaleType',
  },
  settlementWallets: {
    defaultMessage: 'Purchase options',
  },
  showDetailsLabel: {
    defaultMessage: 'Show details',
  },
  showTrainingLineups: {
    defaultMessage: 'Show training lineups',
  },
  sortBy: {
    defaultMessage: 'Sort by',
  },
  startedOnlyFilterLabel: {
    defaultMessage: 'Not empty',
  },
  startingPrediction: {
    defaultMessage: 'Starting prediction',
    description: 'Filters - StartingPrediction',
  },
  tournament: {
    defaultMessage: 'Tournament',
  },
  watchlist: {
    defaultMessage: 'Watchlists',
    description: 'Filters - Watchlists',
  },
});

/* eslint-disable sort-keys */
export const sorts = defineMessages<
  AlgoliaCardIndexesName | PrintableI18nPlayerSortName | GallerySortName
>({
  'Best Value': {
    defaultMessage: 'Most Relevant',
    description: 'Sorts - Best Value',
  },
  'Cards Highest Average Score': {
    defaultMessage: 'Highest Average Score',
    description: 'Sorts - Cards Highest Average Score',
  },
  'Cards Highest Price': {
    defaultMessage: 'Highest Price',
    description: 'Sorts - Cards Highest Price',
  },
  'Cards Lowest Price': {
    defaultMessage: 'Lowest Price',
    description: 'Sorts - Cards Lowest Price',
  },
  'Sealing Value': {
    defaultMessage: 'Sealing Value',
  },
  New: {
    defaultMessage: 'New',
    description: 'Sorts - New',
  },
  'Special editions': {
    defaultMessage: 'Special editions',
    description: 'Sorts - Special editions',
  },
  'Cards New': {
    defaultMessage: 'New',
    description: 'Sorts - Cards New',
  },
  'Cards On Sale Best Value': {
    defaultMessage: 'Most Relevant',
    description: 'Sorts - Cards On Sale Best Value',
  },
  'Cards On Sale Ending Soon': {
    defaultMessage: 'Ending Soon',
    description: 'Sorts - Cards On Sale Ending Soon',
  },
  'Cards On Sale Highest Average Score': {
    defaultMessage: 'Highest Average Score',
    description: 'Sorts - Cards On Sale Highest Average Score',
  },
  'Cards On Sale Highest Price': {
    defaultMessage: 'Highest Price',
    description: 'Sorts - Cards On Sale Highest Price',
  },
  'Cards On Sale Lowest Price': {
    defaultMessage: 'Lowest Price',
    description: 'Sorts - Cards On Sale Lowest Price',
  },
  'Cards On Sale Newly Listed': {
    defaultMessage: 'Newly listed',
    description: 'Sorts - Cards On Sale Newly Listed',
  },
  'Cards On Sale Popular Player': {
    defaultMessage: 'Popular Player',
    description: 'Sorts - Cards On Sale Popular Player',
  },
  'Cards On Sale Bids Count': {
    defaultMessage: 'Bids Count',
  },
  'Starting Prediction': {
    defaultMessage: 'Highest starting prediction',
    description: 'Sorts - Starting Prediction',
  },
  'Cards On Sale Starting Prediction': {
    defaultMessage: 'Highest starting prediction',
    description: 'Sorts - Cards On Sale Starting Prediction',
  },
  'Cards Player Name': {
    defaultMessage: 'Player Name',
  },
  'Ending Soon': {
    defaultMessage: 'Ending Soon',
    description: 'Sorts - Ending Soon',
  },
  'Highest Average Score': {
    defaultMessage: 'Highest Average Score',
    description: 'Sorts - Highest Average Score',
  },
  'Highest Price': {
    defaultMessage: 'Highest Price',
    description: 'Sorts - Highest Price',
  },
  'Lowest Price': {
    defaultMessage: 'Lowest Price',
    description: 'Sorts - Lowest Price',
  },
  'Newly Listed': {
    defaultMessage: 'Newly listed',
    description: 'Sorts - Newly Listed',
  },
  'Popular Player': {
    defaultMessage: 'Popular Player',
    description: 'Sorts - Popular Player',
  },
  'DESC-L10': {
    defaultMessage: 'Highest Average Score',
    description: 'Sorts - DESC-L10',
  },
  'DESC-L15': {
    defaultMessage: 'Highest Average Score',
    description: 'Sorts - DESC-L15',
  },
  'ASC-player_name': {
    defaultMessage: 'Player Name (A-Z)',
    description: 'Sorts - ASC-player_name',
  },
  'DESC-player_name': {
    defaultMessage: 'Player Name (Z-A)',
    description: 'Sorts - DESC-player_name',
  },
  'ASC-name_for_sorting': {
    defaultMessage: 'Player Name (A-Z)',
    description: 'Sorts - ASC-name_for_sorting',
  },
  'DESC-name_for_sorting': {
    defaultMessage: 'Player Name (Z-A)',
    description: 'Sorts - DESC-name_for_sorting',
  },
  'Cards Lowest Average Score': {
    defaultMessage: 'Lowest Average Score',
    description: 'Sorts - Cards Lowest Average Score',
  },
  Tier: {
    defaultMessage: 'Highest Tier',
    description: 'Sorts - Tier',
  },
  'Tier Desc': {
    defaultMessage: 'Lowest Tier',
    description: 'Sorts - Tier Desc',
  },
});
/* eslint-enable sort-keys */

export const playerUnavailability = defineMessages({
  active: {
    defaultMessage: 'Active',
  },
  expectedReturnDate: {
    defaultMessage: 'Expected return date: {date, date, long}',
  },
  gamesUnavailableCount: {
    defaultMessage:
      '({gamesCount, plural, one {1 {competition} game} other {# {competition} games}})',
  },
  inactive: {
    defaultMessage: 'Inactive',
  },
  injuryTitle: {
    defaultMessage: 'Potential injury',
  },
  suspendedTitle: {
    defaultMessage: 'Suspended',
    description: 'Player Unavailability - SuspendedTitle',
  },
  unavailableSince: {
    defaultMessage: '(since {unavailableSince, date, long})',
  },
  unknownReturnDate: {
    defaultMessage: 'Unknown return date',
  },
});

export const playerGameStatusLabels = defineMessages({
  did_not_play: {
    defaultMessage: 'Did not play',
  },
  did_not_play_short: {
    defaultMessage: 'DNP',
  },
  low_coverage: {
    defaultMessage: 'Low Coverage',
  },
  low_coverage_detail: {
    defaultMessage:
      'Only Decisive Actions (eg goals, assists) will be scored for this game.',
  },
  no_game: {
    defaultMessage: 'No game (NG)',
  },
  no_game_short: {
    defaultMessage: 'NG',
  },
  not_covered: {
    defaultMessage: 'Not Covered',
  },
  pending: {
    defaultMessage: 'Match not yet started',
  },
  reviewing: {
    defaultMessage: 'Score waiting for review',
  },
  uncertain_coverage: {
    defaultMessage: 'Delayed Coverage',
  },
  uncertain_coverage_detail: {
    defaultMessage:
      'Full player data (All-Around scores + Decisive Scores) for this game will not be provided in real-time. It should update within 48 hours after full-time. However, if full player stats are not provided by our data partner before we finalise scoring at the end of the Game Week (targeted at 8pm CET at the latest), only decisive stats will be scored.',
  },
});

export const playerDetails = defineMessages({
  birthDateAndAge: {
    defaultMessage: 'Date of birth (Age)',
  },
  breakdown: {
    defaultMessage: 'Breakdown',
  },
  drawn: {
    defaultMessage: 'D',
  },
  gamesCount: {
    defaultMessage: '{count, plural, one {# game} other {# games}}',
  },
  gamesLeftCount: {
    defaultMessage: '{count, plural, one {# game left} other {# games left}}',
  },
  goalsAgainst: {
    defaultMessage: 'GA',
  },
  goalsDiff: {
    defaultMessage: 'GF',
    description: 'Player Details - GoalsDiff',
  },
  goalsFor: {
    defaultMessage: 'GF',
    description: 'Player Details - GoalsFor',
  },
  injuryReport: {
    defaultMessage: 'Injury report',
  },
  leagueRanking: {
    defaultMessage: 'League ranking',
  },
  lost: {
    defaultMessage: 'L',
  },
  noGames: {
    defaultMessage: 'No games',
  },
  notStarting: {
    defaultMessage: 'Not Starting',
  },
  performance: {
    defaultMessage: 'Performance',
  },
  played: {
    defaultMessage: 'PL',
  },
  player_status: {
    defaultMessage: 'Player status',
  },
  points: {
    defaultMessage: 'PTS',
  },
  starting: {
    defaultMessage: 'Starting',
  },
  upcomingMatches: {
    defaultMessage: 'Upcoming matches',
  },
  upcoming_games: {
    defaultMessage: 'Upcoming games',
  },
  won: {
    defaultMessage: 'W',
  },
});

export const mobileApp = defineMessages({
  disabledSale: {
    defaultMessage: 'Sell (disabled in app)',
  },
  disabledTrade: {
    defaultMessage: 'Trade (disabled in app)',
  },
  openWebMarketplace: {
    defaultMessage: 'Visit Market',
  },
  startTrading: {
    defaultMessage: 'To start trading, visit Sorare on web',
  },
  unavailable: {
    defaultMessage: 'Unavailable',
  },
});

export const wallet = defineMessages({
  addFunds: {
    defaultMessage: 'Add funds',
  },
  disconnect: {
    defaultMessage: 'Disconnect from Wallet',
  },
  emptyWallet: {
    defaultMessage:
      'Please transfer funds to your external wallet: it is currently empty.',
  },
  signTransaction: {
    defaultMessage: 'Please sign the transaction with your wallet.',
  },
  sixDigitsCode: {
    defaultMessage: 'Six-digit code (Two-factor Authentication)',
  },
  walletIsLockedTitle: {
    defaultMessage: 'Unlock your wallet',
  },
});

export const payment = defineMessages({
  applePay: {
    defaultMessage: 'Apple Pay',
  },
  balance: {
    defaultMessage: 'Balance',
  },
  confirmAndPay: {
    defaultMessage: 'Confirm and Pay now',
  },
  confirmationTimeout: {
    defaultMessage:
      'Payment is taking longer then expected to be processed, please wait a few minutes and retry later if this transaction is not visible in your transaction history.',
  },
  conversionCreditDefinition: {
    defaultMessage:
      'Credits apply to a percentage of your card(s) purchase, up to a specified limit. Credits are automatically applied using the maximum eligible amount available, with the closest expiration date used first.',
  },
  googlePay: {
    defaultMessage: 'Google Pay',
  },
  linkCo: {
    defaultMessage: 'Link',
  },
  minimumAmount: {
    defaultMessage: 'Minimum amount: {amount}',
  },
  payment: {
    defaultMessage: 'Payment',
  },
  paymentBoxTitle: {
    defaultMessage: 'Review & Pay',
  },
  peggingDescription: {
    defaultMessage:
      'The price of the cards you list for sale will be tied to {cryptocurrencySymbol}.',
  },
  peggingTitle: {
    defaultMessage: 'Peg card prices to {cryptocurrencySymbol}',
  },
  seller: {
    defaultMessage: 'Seller',
  },
  sorareCashWallet: {
    defaultMessage: 'Cash Wallet',
  },
  sorareCryptoWallet: {
    defaultMessage: '{cryptocurrencySymbol} Wallet',
  },
  sorareWallet: {
    defaultMessage: 'Sorare Wallet',
  },
  wallet: {
    defaultMessage: 'Wallet',
  },
});

export const transferMarket = defineMessages({
  bundle: {
    defaultMessage: 'Bundle',
  },
  bundles: {
    defaultMessage: 'Bundles',
  },
  buyMore: {
    defaultMessage: 'Buy more',
  },
  buySellAndTradeCards: {
    defaultMessage: 'Buy, sell and trade cards',
  },
  directOffer: {
    defaultMessage: 'Direct offer',
  },
  favorites: {
    defaultMessage: 'My Favorites',
  },
  instantBuy: {
    defaultMessage: 'Instant Buy',
  },
  leftAtThisPrice: {
    defaultMessage:
      '{count, plural, one {{remaining} left at {price}} other {{remaining} left at {price}}}',
    description:
      'Instant Buy stepped campaign — units remaining at current price',
  },
  matchdayPicks: {
    defaultMessage: 'Matchday picks',
  },
  new: {
    defaultMessage: 'New Card Auctions',
  },
  orderSummary: {
    defaultMessage: 'Order Summary',
  },
  priceGraph: {
    defaultMessage: 'Price graph',
  },
  publicOffer: {
    defaultMessage: 'Public offer',
  },
  singleCards: {
    defaultMessage: 'Single cards',
  },
  starterPacks: {
    defaultMessage: 'Starter Packs',
  },
  transfer: {
    defaultMessage: 'Manager Sales',
    description: 'Transfer Market - Transfer',
  },
  transferDescription: {
    defaultMessage: 'Buy, sell, and trade cards with other Managers.',
  },
});

export const fiatWallet = defineMessages({
  activateCashWallet: {
    defaultMessage: 'Activate my Cash Wallet',
  },
  activateCashWalletAndAddAnId: {
    defaultMessage: 'Activate Cash Wallet and add an ID',
  },
  addCash: {
    defaultMessage: 'Add cash',
  },
  addMyId: {
    defaultMessage: 'Add my ID',
  },
});

export const gameStatusMessages: {
  [key in GameStatus | 'live']: MessageDescriptor;
} = defineMessages({
  canceled: {
    defaultMessage: 'Canceled',
    description: 'gameStatusMessages - Canceled',
  },
  cancelled: {
    defaultMessage: 'Canceled',
    description: 'gameStatusMessages - Cancelled',
  },
  delayed: {
    defaultMessage: 'Delayed',
  },
  live: {
    defaultMessage: 'Live',
    description: 'Game Status Messages - Live',
  },
  played: {
    defaultMessage: 'Full Time',
  },
  playing: {
    defaultMessage: 'Playing',
  },
  postponed: {
    defaultMessage: 'Postponed',
  },
  scheduled: {
    defaultMessage: 'Scheduled',
  },
  suspended: {
    defaultMessage: 'Suspended',
    description: 'Game Status Messages - Suspended',
  },
});

export const shopCategories: { [key in ShopItemType]?: MessageDescriptor } =
  defineMessages({
    [ShopItemType.BANNER]: {
      defaultMessage: 'My Banners',
    },
    [ShopItemType.SHIELD]: {
      defaultMessage: 'My Badges',
    },
    [ShopItemType.BACKGROUND]: {
      defaultMessage: 'My stadiums',
    },
    [ShopItemType.CLUB_BADGE]: {
      defaultMessage: 'My Club Badges',
    },
    [ShopItemType.ACHIEVEMENT_BADGE]: {
      defaultMessage: 'My Achievement Badges',
    },
    [ShopItemType.EXTRA_SWAP]: {
      defaultMessage: 'My Extra Swap',
    },
    [ShopItemType.EXTRA_TEAMS_CAP]: {
      defaultMessage: 'My Extra Teams',
    },
    [ShopItemType.LOGO]: {
      defaultMessage: 'My Logos',
    },
    [ShopItemType.LEVEL_UP]: {
      defaultMessage: 'My level ups',
    },
    [ShopItemType.JERSEY]: {
      defaultMessage: 'My Jerseys',
    },
    [ShopItemType.DELIVERABLE_WITH_NO_VARIANT]: {
      defaultMessage: 'My Caps',
    },
  });

export const seasonEligibilityMessages: {
  [key in SeasonEligibility]: MessageDescriptor;
} = defineMessages({
  [SeasonEligibility.IN_SEASON]: {
    defaultMessage: 'In Season',
  },
  [SeasonEligibility.CLASSIC]: {
    defaultMessage: 'Classic',
  },
});

export const referralStateMessages = defineMessages<ReferralState>({
  [ReferralState.ALL]: {
    defaultMessage: 'All states',
  },
  [ReferralState.COMPLETED]: {
    defaultMessage: 'Successful',
  },
  [ReferralState.EXPIRED]: {
    defaultMessage: 'Expired',
    description: 'Referral State Messages - Expired',
  },
  [ReferralState.IN_PROGRESS]: {
    defaultMessage: 'In progress',
  },
  [ReferralState.PENDING_REVIEW]: {
    defaultMessage: 'Pending review',
  },
  [ReferralState.PENDING_REFEREE_CONFIRMATION]: {
    defaultMessage: 'Pending confirmation',
  },
  [ReferralState.BLOCKED]: {
    defaultMessage: 'Suspended',
    description: 'Referral State Messages - Blocked',
  },
});

export const inventoryMessages = defineMessages({
  allTeams: {
    defaultMessage: 'All teams',
  },
  noRevealableCards: {
    defaultMessage: 'No cards to reveal',
  },
  noRevealableMatchingCards: {
    defaultMessage: 'No revealable cards matching "{query}"',
  },
  revealAll: {
    defaultMessage: 'Reveal all cards',
  },
  revealAllConfirmTitle: {
    defaultMessage: 'Are you sure?',
  },
  searchPlaceholder: {
    defaultMessage: 'Search by player, team, position...',
  },
});

export const invoiceTableMessages = defineMessages({
  fees: {
    defaultMessage: 'Fees',
  },
  subtotal: {
    defaultMessage: 'Subtotal',
  },
});

export const commonMarketplace = defineMessages({
  addNotAvailable: {
    defaultMessage: 'Not available',
    description: 'Common Marketplace - AddNotAvailable',
  },
  drop: {
    defaultMessage: 'Drop',
  },
  dropNotAvailable: {
    defaultMessage: 'Not available',
    description: 'Common Marketplace - DropNotAvailable',
  },
});

export const scoutingCenter = defineMessages({
  advancedView: {
    defaultMessage: 'Advanced view',
  },
  decisiveActions: {
    defaultMessage: 'Decisive actions',
  },
  findTheBestPlayers: {
    defaultMessage: 'Find the best players',
  },
  last10Total: {
    defaultMessage: 'Last 10 total',
  },
  last15Total: {
    defaultMessage: 'Last 15 total',
  },
  myWatchlists: {
    defaultMessage: 'My Watchlists',
  },
  per90: {
    defaultMessage: 'Per 90’',
  },
  perGame: {
    defaultMessage: 'Per game',
  },
  projectionClassic: {
    defaultMessage: 'Next Game Week projection',
    description: 'Scouting Center - Projection Classic',
  },
  projectionClassicTooltip: {
    defaultMessage: 'Next Game Week projection {rotowireLogo}',
  },
  projectionDaily: {
    defaultMessage: 'Next Daily projection',
    description: 'Scouting Center - Projection Daily',
  },
  projectionDailyTooltip: {
    defaultMessage: 'Next Daily projection {rotowireLogo}',
  },
  projectionDifferenceClassic: {
    defaultMessage:
      'Difference between Game Week projection and L10 Average Score',
  },
  projectionDifferenceDaily: {
    defaultMessage: 'Difference between daily projection and L10 Average Score',
  },
  projectionGrade: {
    defaultMessage: 'PG',
  },
  projectionGradeClassic: {
    defaultMessage: 'Next Game Week projection grade',
    description: 'Scouting Center - Projection Grade Classic',
  },
  projectionGradeClassicTooltip: {
    defaultMessage: 'Next Game Week projection grade',
    description: 'Scouting Center - Projection Grade Classic Tooltip',
  },
  projectionGradeDaily: {
    defaultMessage: 'Next Daily projection grade',
    description: 'Scouting Center - Projection Grade Daily',
  },
  projectionGradeDailyTooltip: {
    defaultMessage: 'Next Daily projection grade',
    description: 'Scouting Center - Projection Grade Daily Tooltip',
  },
  scout: {
    defaultMessage: 'Scout',
  },
  scoutNow: {
    defaultMessage: 'Scout Now',
  },
  scoutingCenter: {
    defaultMessage: 'Scouting Center',
  },
  statistics: {
    defaultMessage: 'Statistics',
  },
  stats: {
    defaultMessage: 'Stats',
    description: 'Scouting Center - Stats',
  },
  watchlists: {
    defaultMessage: 'Watchlists',
    description: 'Scouting Center - Watchlists',
  },
});

export const cart = defineMessages({
  add: {
    defaultMessage: 'Add',
    description: 'Cart - Add',
  },
  addAllToCart: {
    defaultMessage: 'Add all to cart',
  },
  addToCart: {
    defaultMessage: 'Add to cart',
  },
  addToLineup: {
    defaultMessage: 'Add to lineup',
  },
  addedToCart: {
    defaultMessage: 'Added to cart',
  },
  cart: {
    defaultMessage: 'Cart',
  },
  checkout: {
    defaultMessage: 'Checkout',
  },
  clearAll: {
    defaultMessage: 'Clear all',
  },
  creditApplied: {
    defaultMessage: 'Credit applied',
  },
  creditAvailable: {
    defaultMessage: 'Credit available',
  },
  inCart: {
    defaultMessage: 'In Cart',
  },
  saveForLater: {
    defaultMessage: 'Save for later',
  },
  savedForLater: {
    defaultMessage: 'Saved for later',
  },
  soldOut: {
    defaultMessage: 'Sold out',
  },
  total: {
    defaultMessage: 'Total',
    description: 'Cart - Total',
  },
});

export const marketCredits = defineMessages({
  description: {
    defaultMessage:
      '{percentage, number, percent} discount up to {maxDiscount} off',
  },
  haveDiscountCredits: {
    defaultMessage:
      'Your {percentageDiscount, number, percent} market credits (up to { maxDiscount }) is expiring in just {timeLeft}! Take a peek <walletLink>in your wallet</walletLink> to view all your available credits.',
  },
  marketCredits: {
    defaultMessage: 'Market credits',
  },
});

export const congratsScreens = defineMessages({
  newSigning: {
    defaultMessage:
      'New{br}{cardsCount, plural, =1 {signing} other {signings}}',
  },
  welcomeMultiplePlayersToYourTeam: {
    defaultMessage:
      'Welcome to the Squad! {cardsCount} New Players Now Part of Your Team!',
  },
  welcomePlayerToYourTeam: {
    defaultMessage: 'Welcome {playerDisplayName} to your team!',
  },
});

export const onboarding = defineMessages({
  discount: {
    defaultMessage: 'Get {percentageDiscount, number, percent} off',
  },
  expert: {
    defaultMessage: 'Expert',
  },
  expertDescription: {
    defaultMessage: 'Skip onboarding and go to the Market',
  },
});

export const wheel = defineMessages({
  bestFiveClue: {
    defaultMessage: 'BEST 5 CLUE',
  },
  bestStarRankClue: {
    defaultMessage: 'HIGHEST RANK CLUE',
  },
  competitionClue: {
    defaultMessage: 'COMPETITION CLUE',
  },
  countryClue: {
    defaultMessage: 'COUNTRY CLUE',
  },
  divisionClue: {
    defaultMessage: 'DIVISION CLUE',
  },
  gems: {
    defaultMessage: 'Gems',
    description: 'Wheel - Gems',
  },
  howToWin: {
    defaultMessage: 'How to win tickets?',
  },
  nothing: {
    defaultMessage: 'Miss!',
  },
  positionClue: {
    defaultMessage: 'POSITION CLUE',
  },
  potionClue: {
    defaultMessage: 'POTION CLUE',
  },
  raffleTicket: {
    defaultMessage:
      '{amount, plural, one {RAFFLE TICKET} other {RAFFLE TICKETS}}',
  },
  shuffleClue: {
    defaultMessage: 'SNOWGLOBE SHUFFLE CLUE',
  },
  spin: {
    defaultMessage: 'Spin',
  },
  spinAgain: {
    defaultMessage: 'Spin again',
  },
});

export const cardPull = defineMessages({
  bestStarRankClue: {
    defaultMessage: 'Reveal Highest rank',
  },
  clues: {
    defaultMessage: 'Clues',
  },
  competitionClue: {
    defaultMessage: 'Competition',
    description: 'Card Pull - Competition Clue',
  },
  cost: {
    defaultMessage: 'Cost x{cost}',
  },
  countryClue: {
    defaultMessage: 'Country',
    description: 'Card Pull - Country Clue',
  },
  divisionClue: {
    defaultMessage: 'Division',
  },
  drawAndOdds: {
    defaultMessage: 'Draw and odds',
  },
  fiftyFiftyClue: {
    defaultMessage: 'Best 5',
  },
  globalCupClue: {
    defaultMessage: 'Global Cup',
  },
  myEssence: {
    defaultMessage: 'My Essence',
  },
  positionClue: {
    defaultMessage: 'Position',
    description: 'Card Pull - Position Clue',
  },
  rulesAndEligibility: {
    defaultMessage: 'Rules & Eligibility',
  },
  shuffleClue: {
    defaultMessage: 'Snowglobe Shuffle',
  },
});

export const shortCardPackcategoryLabels = defineMessages({
  [CardPackCategory.GOLD]: {
    defaultMessage: 'Gold',
  },
  [CardPackCategory.SILVER]: {
    defaultMessage: 'Silver',
  },
  [CardPackCategory.BRONZE]: {
    defaultMessage: 'Bronze',
  },
  [CardPackCategory.STARTER]: {
    defaultMessage: 'Starter',
  },
  [CardPackCategory.ONLY_STARTING_PLAYERS_TEMPLATE]: {
    defaultMessage: 'Starting Players Only',
  },
}) as Record<CardPackCategory, MessageDescriptor>;
